import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Modal from '@mui/material/Modal';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Logo from '../../assets/img/logo.png';
import bgImage from '../../assets/img/bg.png';
import Profile from '../../assets/img/profile.png';
import authApi from '../../services/auth';
import businessApi from '../../services/business_selection';
import { setEncryptedCookie, getDecryptedCookie, removeCookie, removeNonBaseDomainCookies } from '../../utils';
import { setBusiness } from '../../redux/business/actions';
import userApi from '../../services/users';
import menuApi from '../../services/menu';
import { useDispatch, useSelector } from 'react-redux';
import { showLoader, hideLoader } from '../../redux/loader/actions';
import { showSnackbar } from '../../redux/snackbar/actions';
import { setAuth } from '../../redux/common/actions';
import { Formik, Field, Form } from 'formik';
import { setMenuItems, clearMenuItems } from '../../redux/menu/actions';
import { setOrganization } from '../../redux/organization/actions';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import BusinessSelection from '../signin/business_selection';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';

const Navbar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const organizationItems = useSelector((state) => state.organizationReducer.items);
    const uid = getDecryptedCookie('ssoid');
    const uuids = getDecryptedCookie('uuid');
    const handleSessionExpiredClose = () => {
        setIsSessionExpiredModalOpen(false);
        handleLogout();
    };

    const [anchorEl, setAnchorEl] = useState(null);
    const [businessCount, setBusinessCount] = useState([]);
    const [organisationData, setOrganisationData] = useState([]);
    const [uuid, setUuid] = useState(null);
    const [id, setId] = useState();
    const [selectedOption, setSelectedOption] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [verifyModal, setVerifyModal] = useState(false);
    const [loginFailedModal, setLoginFailedModal] = useState(false);
    const [isSessionExpiredModalOpen, setIsSessionExpiredModalOpen] = useState(false);

    const scheduleTokenRefresh = () => {
        const refreshAccessToken = async () => {
            try {
                await authApi.refreshToken();
            } catch (err) {
                console.error('Failed to refresh access token:', err);
            }
        };

        refreshAccessToken();
        setInterval(refreshAccessToken, 14 * 60 * 1000);
    };
    useEffect(() => {
        const timer = setTimeout(() => {
            scheduleTokenRefresh();
            validateUserToken();
        }, 2000);
        return () => clearTimeout(timer);
        removeNonBaseDomainCookies();
        window.location.reload();
    }, []);

    const fetchBusiness = useCallback(async () => {
        dispatch(showLoader());
        try {
            const response = await businessApi.getAllBusiness();
            const responseData = response?.data?.data;

            if (responseData) {
                setBusinessCount(responseData);
            } else {
                console.warn('Unexpected response format:', responseData);
            }
        } catch (error) {
            console.error('Error fetching businesses:', error);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch]);

    const handleLogout = () => {
        dispatch(showLoader());
        const onSuccess = (res) => {
            dispatch(hideLoader());
            if (res?.data?.status === true) {
                dispatch(
                    showSnackbar({
                        message: res?.data?.msg,
                        autoHideDuration: 3000,
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                        },
                        variant: 'success'
                    })
                );
                navigate('/');
                setAnchorEl(null);
                dispatch(setAuth({ isAuthenticated: false }));
                removeCookie('abc');
                removeCookie('at');
                removeCookie('bi');
                removeCookie('buslap');
                removeCookie('ci');
                removeCookie('created_by');
                removeCookie('def');
                removeCookie('fdf');
                removeCookie('id');
                removeCookie('name');
                removeCookie('rt');
                removeCookie('shop_id');
                removeCookie('token');
                removeCookie('uuid');
                removeCookie('action');
                removeCookie('proid');
                removeCookie('bss');
                removeCookie('upgrade');
                removeCookie('upid');
                removeCookie('ssoid');
                removeCookie('ssoUid');
                removeCookie('abc');
                removeCookie('buslap');
                removeCookie('fdf');
                removeCookie('def');
                removeCookie('id');
                removeCookie('ids');
                removeCookie('name');
                removeCookie('proid');
                removeCookie('bss');
                removeCookie('bas');
                removeCookie('bass');
            }
        };
        const onFailure = (err) => {
            dispatch(hideLoader());
            dispatch(
                showSnackbar({
                    message: err?.response?.data?.message || 'Failed to fetch data',
                    autoHideDuration: 3000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right'
                    },
                    variant: 'error'
                })
            );
        };

        authApi.logoutUser().then(onSuccess, onFailure);
        handleMenuClose();
    };
    const validateUserToken = () => {
        dispatch(showLoader());
        const onSuccess = (res) => {
            dispatch(hideLoader());
            if (res?.data?.status === true) {
                fetchBusiness();
                editUserById(res?.data?.uuid);
                if (!res?.data?.has_access_in_current_id && res?.data?.has_access_in_other_id) {
                    setVerifyModal(true);
                }
                if (!res?.data?.has_access_in_current_id && !res?.data?.has_access_in_other_id) {
                    setLoginFailedModal(true);
                }
            }
            dispatch(
                setAuth({
                    isAuthenticated: true
                })
            );
            fetchBusiness();
        };
        const onFailure = (err) => {
            dispatch(hideLoader());
        };
        authApi.validateToken().then(onSuccess, onFailure);
    };

    const handleSettings = () => {
        navigate('/changecredentails');
        setAnchorEl(null);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSelectChange = async (selectedBusinessId, setFieldValue) => {
        // Clear cookies
        removeCookie('abc');
        removeCookie('bi');
        removeCookie('print');
        removeCookie('buslap');
        removeCookie('ci');
        removeCookie('created_by');
        removeCookie('def');
        removeCookie('fdf');
        removeCookie('id');
        removeCookie('name');
        removeCookie('shop_id');
        removeCookie('token');
        removeCookie('uuid');

        // Update session
        sessionStorage.setItem('organisationId', selectedBusinessId);

        // Update state
        setUuid(selectedBusinessId);
        dispatch(setOrganization(Math.random()));
        editUserById(selectedBusinessId);
        handleUserSubmit(selectedBusinessId);

        if (setFieldValue) {
            setFieldValue('business', selectedBusinessId);
        }
    };
    const handleUserSubmit = (userId) => {
        dispatch(showLoader());
        const onSuccess = (res) => {
            dispatch(hideLoader());
            setTimeout(() => {
                navigate('/home');
            }, 2000);
        };
        const onFailure = (err) => {
            dispatch(hideLoader());
        };
        authApi.userVerify(userId).then(onSuccess, onFailure);
    };

    const getAllMenu = useCallback(
        (id) => {
            if (!id) return;
            dispatch(showLoader());

            const onSuccess = (res) => {
                dispatch(hideLoader());
                dispatch(clearMenuItems());
                if (!res?.data?.status) {
                    setModalMessage('Failed to fetch menus for this business.');
                    setIsModalOpen(true);
                } else {
                    const items = res.data.data || [];

                    if (items.length === 0) {
                        setModalMessage('No menus available for this business.');
                        setIsModalOpen(true);
                    } else {
                        dispatch(setMenuItems(items));
                    }
                }
            };

            const onFailure = (err) => {
                dispatch(hideLoader());
                console.error('Error fetching menu:', err);
                setModalMessage(
                    'No Roles is associated with this organization. Please select a different organization.'
                );
                setIsModalOpen(true);
            };

            menuApi.userBasedMenu(id).then(onSuccess, onFailure);
        },
        [dispatch]
    );

    useEffect(() => {
        const intervalId = setInterval(() => {
            const sso = getDecryptedCookie('ssoid');
            if (!sso) {
                handleLogout();
                navigate('/');
            }
        }, 1000);
        return () => clearInterval(intervalId);
    }, [navigate]);

    useEffect(() => {
        if (uuid) {
            setIsModalOpen(false);
        }
    }, [uuid]);

    const editUserById = async (userId) => {
        dispatch(showLoader());
        try {
            const response = await userApi.getUserId(userId);
            removeCookie('uuid');
            if (response?.data?.status) {
                setEncryptedCookie('abc', response.data.data[0].role.uuid);
                setEncryptedCookie('name', response.data.data[0].user.organization_name);
                setEncryptedCookie('fdf', response.data.data[0].user.username, {
                    expires: 7
                });
                setEncryptedCookie('uuid', response.data.data[0].user.uuid);
                getAllMenu(response.data.data[0].role.uuid);
                const id = response.data.data[0].role.uuid;
                setEncryptedCookie('id', id, { expires: 7 });

                // Update session if needed
                sessionStorage.setItem('organisationId', response.data.data[0].user.uuid);
            } else {
                console.error('Error fetching user data');
            }
        } catch (error) {
            console.error('Error fetching user:', error);
        } finally {
            dispatch(hideLoader());
        }
    };
    const findMatchedBusiness = (businesses, organizationId) => {
        return businesses.find((business) => business.uuid === organizationId);
    };

    const getOrganisation = async (id) => {
        dispatch(showLoader());
        try {
            const res = await userApi.getUserById(id);
            if (res?.data?.status) {
                setOrganisationData(res.data.data);
            } else {
                console.error('Error fetching user data');
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            handleLogout();
        } finally {
            dispatch(hideLoader());
        }
    };

    useEffect(() => {
        const nameoforg = sessionStorage.getItem('organisationId');
        if (businessCount.length > 0 && nameoforg) {
            const matchedBusiness = findMatchedBusiness(businessCount, nameoforg);
            if (matchedBusiness) {
                setUuid(matchedBusiness.uuid);
                // Update any other related state if needed
            }
        }
    }, [businessCount, sessionStorage.getItem('organisationId')]);
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'organisationId') {
                const newOrgId = event.newValue;
                if (businessCount.length > 0 && newOrgId) {
                    const matchedBusiness = findMatchedBusiness(businessCount, newOrgId);
                    if (matchedBusiness) {
                        setUuid(matchedBusiness.uuid);
                        // You might want to trigger other updates here
                    }
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [businessCount]);

    useEffect(() => {
        if (id) {
            getOrganisation(id);
        }
    }, [id]);

    useEffect(() => {
        fetchBusiness();
    }, [fetchBusiness]);

    return (
        <>
            <AppBar
                position='static'
                sx={{
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    margin: 0,
                    padding: 0,
                    boxShadow: 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right',
                    zIndex: '100'
                }}
            >
                <Container maxWidth='xl' sx={{ padding: 0, margin: 0 }}>
                    <Toolbar disableGutters sx={{ padding: 0, margin: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#fff',
                                padding: isMobile ? '5px' : '5px',
                                borderRadius: '0 50px 50px 0',
                                marginLeft: isMobile ? '0' : '-25px'
                            }}
                        >
                            <Typography variant='h6' noWrap component='div' sx={{ padding: 0, margin: 0 }}>
                                <img src={Logo} alt='Logo' style={{ height: isMobile ? '35px' : '55px' }} />
                            </Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />
                        <Box sx={{ flexGrow: 0 }}>
                            <IconButton sx={{ p: 0, marginRight: 2 }}>
                                <MenuItem>
                                    {businessCount?.length > 1 ? (
                                        <Formik
                                            initialValues={{ business: uuid || '' }}
                                            onSubmit={(values) => handleSelectChange(values.business)}
                                        >
                                            {({ setFieldValue }) => (
                                                <Form>
                                                    <FormControl
                                                        sx={{
                                                            minWidth: 200,
                                                            textAlign: 'left',
                                                            '@media (max-width: 375px)': {
                                                                minWidth: 120
                                                            }
                                                        }}
                                                    >
                                                        <Select
                                                            labelId='demo-simple-select-label'
                                                            size='small'
                                                            id='demo-simple-select'
                                                            value={
                                                                uuid || sessionStorage.getItem('organisationId') || ''
                                                            }
                                                            onChange={(e) => {
                                                                const selectedValue = e.target.value;
                                                                setFieldValue('business', selectedValue);
                                                                handleSelectChange(selectedValue, setFieldValue);
                                                            }}
                                                            disableUnderline
                                                            sx={{
                                                                backgroundColor: 'transparent',
                                                                border: 'none',
                                                                color: 'white',
                                                                '& .MuiSelect-icon': {
                                                                    color: 'white'
                                                                }
                                                            }}
                                                        >
                                                            {businessCount.map((data) => (
                                                                <MenuItem key={data.id} value={data.uuid}>
                                                                    {data?.organization_name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Form>
                                            )}
                                        </Formik>
                                    ) : (
                                        businessCount.map((data) => (
                                            <Typography
                                                variant='subtitle2'
                                                sx={{
                                                    color: 'white',
                                                    fontWeight: '600',
                                                    fontSize: '16px'
                                                }}
                                            >
                                                {data?.organization_name}
                                            </Typography>
                                        ))
                                    )}
                                </MenuItem>

                                <Avatar
                                    alt='Profile Picture'
                                    src={Profile}
                                    sx={{ border: '3px solid #fff', boxShadow: '0 0 10px' }}
                                    onClick={handleMenuOpen}
                                />
                            </IconButton>
                            <Menu
                                sx={{ mt: '45px' }}
                                id='menu-appbar'
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleSettings}>
                                    <Typography textAlign='center'>Settings</Typography>
                                </MenuItem>
                                <MenuItem onClick={handleLogout}>
                                    <Typography textAlign='center'>Logout</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Modal
                open={isSessionExpiredModalOpen}
                onClose={() => {}}
                aria-labelledby='session-expired-modal'
                aria-describedby='session-expired-description'
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        width: '80%',
                        maxWidth: 400,
                        padding: 4,
                        textAlign: 'center'
                    }}
                >
                    <Typography variant='h6' id='session-expired-modal'>
                        Session Expired
                    </Typography>
                    <Typography variant='body1' id='session-expired-description' sx={{ marginBottom: 2 }}>
                        Your session has expired. Please log in again.
                    </Typography>
                    <Button variant='contained' color='primary' onClick={handleSessionExpiredClose}>
                        OK
                    </Button>
                </Box>
            </Modal>
            <Dialog
                open={verifyModal}
                maxWidth='sm'
                fullWidth
                BackdropProps={{
                    sx: {
                        backdropFilter: 'blur(6px)', // You can adjust the blur intensity
                        backgroundColor: 'rgba(0, 0, 0, 0.4)' // Optional: add a semi-transparent overlay
                    }
                }}
            >
                <BusinessSelection
                    BackdropProps={{
                        sx: {
                            backdropFilter: 'blur(6px)', // You can adjust the blur intensity
                            backgroundColor: 'rgba(0, 0, 0, 0.4)' // Optional: add a semi-transparent overlay
                        }
                    }}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                />
            </Dialog>
            <Dialog
                open={loginFailedModal}
                maxWidth='lg'
                fullWidth
                BackdropProps={{
                    sx: {
                        backdropFilter: 'blur(6px)', // You can adjust the blur intensity
                        backgroundColor: 'rgba(0, 0, 0, 0.4)' // Optional: add a semi-transparent overlay
                    }
                }}
            >
                <DialogTitle textAlign={'center'}>
                    <ErrorOutlinedIcon sx={{ color: 'warning.main', fontSize: 50 }} />
                    <Typography fontSize={20} fontWeight={600} color='primary'>
                        Login Failed
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography fontSize={18}>
                        👉 Your login attempt was unsuccessful. Please review the following possible reasons:
                    </Typography>
                    <Typography fontSize={18}>
                        👉 Incorrect Email or Password – Ensure your credentials are entered correctly.
                    </Typography>
                    <Typography fontSize={18}>
                        👉 Email Not Verified – If this is your first login, please verify your email through the link
                        sent to your inbox.
                    </Typography>
                    <Typography fontSize={18}>
                        👉 BOC Access Not Granted – Your account may not have access to BOC. Please contact your
                        administrator for access privileges.
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Navbar;
